import { z } from 'zod';

// Canonical warmup-STATE buckets (Phase 7). Replaces the old deliverability
// buckets (`healthy/warming/risk/paused`). These map to the API's
// `statusBucket` (derived from `accountState` via `ACCOUNT_STATE_BUCKET`). The
// internal `setup` bucket (CONNECTED / DNS_CHECKING / DNS_FAILED / DNS_PASSED
// / WARMUP_READY) is NOT a filter option, so it is absent here — but
// `disconnected` (split out of `setup` so a disconnected account is
// independently filterable) is. This changes the `?status=` URL contract:
// old saved links with `healthy/warming/risk` fall back to `undefined` via
// `multiValueFilter`.
export const EMAIL_ACCOUNT_STATUS_FILTERS = [
  'all',
  'active',
  'throttled',
  'paused',
  'suspended',
  'cooldown',
  'maintenance',
  'disconnected',
] as const;
export type EmailAccountStatusFilter = (typeof EMAIL_ACCOUNT_STATUS_FILTERS)[number];

export const EMAIL_ACCOUNT_PROVIDER_FILTERS = [
  'all',
  'gmail',
  'google_workspace',
  'outlook',
  'ms365',
  'azure',
  'others',
  'zoho',
  'yahoo',
  'sendgrid',
  'mailgun',
  'amazon_ses',
  'elastic_email',
  'yandex',
  'godaddy',
] as const;
export type EmailAccountProviderFilter = (typeof EMAIL_ACCOUNT_PROVIDER_FILTERS)[number];

// The selectable (non-`all`) members of each dimension. `status`/`provider` are
// now MULTI-select filters whose URL value is `string[] | undefined` (mirroring
// `tags` below), so the URL never carries the old `'all'` sentinel — an empty
// set means "no filter". The `…_FILTERS` arrays above (with `all`) are still the
// source for the label maps + the bulk DTO mirror; these narrower arrays drive
// the search-schema normalisation + the filter-menu option rows.
export const EMAIL_ACCOUNT_STATUS_VALUES = [
  'active',
  'throttled',
  'paused',
  'suspended',
  'cooldown',
  'maintenance',
  'disconnected',
] as const;
export type EmailAccountStatusValue = (typeof EMAIL_ACCOUNT_STATUS_VALUES)[number];

export const EMAIL_ACCOUNT_PROVIDER_VALUES = [
  'gmail',
  'google_workspace',
  'outlook',
  'ms365',
  'azure',
  'others',
  'zoho',
  'yahoo',
  'sendgrid',
  'mailgun',
  'amazon_ses',
  'elastic_email',
  'yandex',
  'godaddy',
] as const;
export type EmailAccountProviderValue = (typeof EMAIL_ACCOUNT_PROVIDER_VALUES)[number];

// Outreach Readiness filter values (V1.1) — mirrors the backend's
// `OutreachReadinessFilter` enum (same 3 string literals, no translation
// layer, like `EmailAccountSortField`). This is the URL/filter-layer copy;
// the structurally-identical `OutreachReadinessStatus` in `setup-readiness.ts`
// is the client-derivation-layer copy (same split as `EmailAccountStatusValue`
// here vs. `StatusBucket` in `lib/email-accounts.ts`) — both must stay in
// lock-step with the 3 literals, but they're intentionally two types serving
// two layers, not a duplicate to merge.
export const EMAIL_ACCOUNT_OUTREACH_READINESS_VALUES = [
  'ready',
  'warming',
  'not_ready',
] as const;
export type EmailAccountOutreachReadinessValue =
  (typeof EMAIL_ACCOUNT_OUTREACH_READINESS_VALUES)[number];

// Server-sortable columns. Values mirror the TanStack column ids in
// `tanstack/columns.tsx` and the backend `EmailAccountSortField` enum, so the
// `?sort=` URL param round-trips through all three without translation.
export const EMAIL_ACCOUNT_SORT_FIELDS = [
  'email',
  'status',
  'score',
  'createdAt',
] as const;
export type EmailAccountSortField = (typeof EMAIL_ACCOUNT_SORT_FIELDS)[number];

export const EMAIL_ACCOUNT_SORT_DIRS = ['asc', 'desc'] as const;
export type EmailAccountSortDir = (typeof EMAIL_ACCOUNT_SORT_DIRS)[number];

/** Backend default sort — newest connection first. The schema falls back to
 * these so an absent/garbage `?sort=`/`?dir=` matches the server's default. */
export const DEFAULT_SORT_FIELD: EmailAccountSortField = 'createdAt';
export const DEFAULT_SORT_DIR: EmailAccountSortDir = 'desc';

/** Default rows-per-page used by the route loader and the list query. Kept
 * in sync with the backend's default (`limit=25`). */
export const DEFAULT_PAGE_SIZE = 25;

// Normalise a raw URL value (a lone string, a `string[]`, or absent) into a
// deduped subset of `valid`, dropping blanks + unknown members and collapsing
// the empty set to `undefined`. Shared by the `status`/`provider` multi-select
// fields so an inactive filter leaves NO key in the URL (mirrors how `tags`
// collapses) — otherwise TanStack serializes an ugly `status=[]` literal.
export function multiValueFilter<T extends readonly string[]>(valid: T) {
  const allowed = new Set<string>(valid);
  return z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((v) => {
      const raw = Array.isArray(v) ? v : v == null ? [] : [v];
      const seen = new Set<string>();
      const list: T[number][] = [];
      for (const s of raw) {
        const value = s.trim();
        if (allowed.has(value) && !seen.has(value)) {
          seen.add(value);
          list.push(value as T[number]);
        }
      }
      return list.length > 0 ? list : undefined;
    })
    .catch(undefined);
}

export const emailAccountsSearchSchema = z.object({
  // Multi-select status/provider filters — `EmailAccountStatusValue[] | undefined`
  // / `EmailAccountProviderValue[] | undefined`. Normalised like `tags`: a lone
  // value coerces to a single-element array, unknown/blank members are dropped +
  // deduped, and an empty set collapses to `undefined` (no URL key). OR-matched
  // within each dimension on the backend.
  status: multiValueFilter(EMAIL_ACCOUNT_STATUS_VALUES),
  provider: multiValueFilter(EMAIL_ACCOUNT_PROVIDER_VALUES),
  q: z.string().catch(''),
  // `z.coerce.number()` turns the URL-string `?page=2` into a real number;
  // `.catch(default)` keeps URL parsing forgiving so a malformed value falls
  // back to safe defaults rather than crashing the route.
  page: z.coerce.number().int().min(1).catch(1),
  // Capped at 50: the table renders every row (no virtualization), and the
  // frontend-principles skill requires virtualizing lists > 50 items. Holding
  // the page size ≤ 50 keeps the rendered row count under that threshold
  // without pulling in a virtualizer. This is also load-bearing for frame
  // budget, not just payload size: at 50 rows × ~19 visible columns the table
  // renders ~918 cell components, already at the edge of a 16.7ms frame in
  // dev (see PERFORMANCE_AUDIT.md F7). Raising this cap or adding columns to
  // the registry spends that budget — check headroom (or virtualize) first.
  limit: z.coerce.number().int().min(1).max(50).catch(DEFAULT_PAGE_SIZE),
  // Sort is server-driven and URL-persisted (survives refresh + sharing).
  // `.catch(default)` keeps the route forgiving — an unknown column or
  // direction falls back to the backend default instead of erroring.
  sort: z.enum(EMAIL_ACCOUNT_SORT_FIELDS).catch(DEFAULT_SORT_FIELD),
  dir: z.enum(EMAIL_ACCOUNT_SORT_DIRS).catch(DEFAULT_SORT_DIR),
  // Tags filter (TRU-326 interim). TanStack Router JSON-encodes the value, so a
  // populated filter round-trips as a real `string[]`; a lone value may arrive
  // as a string. Normalize to `string[]` (dropping blanks), then collapse the
  // empty set to `undefined` so an inactive filter leaves NO `tags` key in the
  // URL (mirrors how `q`/`status` defaults are omitted) — otherwise TanStack
  // serializes an ugly `tags=[]` literal. `.catch(undefined)` keeps it forgiving.
  // May also carry the reserved `WITHOUT_TAGS_FILTER_VALUE` sentinel.
  tags: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((v) => {
      const list = (Array.isArray(v) ? v : v == null ? [] : [v]).filter(
        (s) => s.trim().length > 0,
      );
      return list.length > 0 ? list : undefined;
    })
    .catch(undefined),
  // ── Filter-modal params (TRU-326) ──────────────────────────────────────────
  // All six collapse to `undefined` when inactive (no URL key) so the URL stays
  // clean and the bulk envelope / list query omit them — mirroring the `tags`
  // pattern above. Numeric bounds are coerced from the URL string + clamped to
  // their server-validated range; out-of-range/garbage falls back to undefined.
  /** ESP mode — single-select. `cfg.espMode = AUTO|CUSTOM` on the backend. */
  esp: z.enum(['auto', 'custom']).optional().catch(undefined),
  /** Warm-up max-limit lower/upper bound (int 5–100). Literals, not
   * `MAX_LIMIT_*` from `filters/filter-helpers.ts`: that module imports this one
   * at runtime, so importing back would be a cycle. */
  maxLimitMin: z.coerce.number().int().min(5).max(100).optional().catch(undefined),
  maxLimitMax: z.coerce.number().int().min(5).max(100).optional().catch(undefined),
  /** Deliverability-rate lower/upper bound (int 0–100). */
  deliverabilityMin: z.coerce.number().int().min(0).max(100).optional().catch(undefined),
  deliverabilityMax: z.coerce.number().int().min(0).max(100).optional().catch(undefined),
  /** Outreach Readiness multi-select (V1.1) — OR-matched on the backend,
   * normalised identically to `status`/`provider` above. */
  outreachReadiness: multiValueFilter(EMAIL_ACCOUNT_OUTREACH_READINESS_VALUES),
  /** Setup Score lower/upper bound (int 0–100, V1.1). */
  setupScoreMin: z.coerce.number().int().min(0).max(100).optional().catch(undefined),
  setupScoreMax: z.coerce.number().int().min(0).max(100).optional().catch(undefined),
  /** Per-ESP percentage bounds (int 0–100), shown under the ESP filter's
   * Custom row builder. Each pair applies independently. */
  espGoogleMin: z.coerce.number().int().min(0).max(100).optional().catch(undefined),
  espGoogleMax: z.coerce.number().int().min(0).max(100).optional().catch(undefined),
  espMicrosoftMin: z.coerce.number().int().min(0).max(100).optional().catch(undefined),
  espMicrosoftMax: z.coerce.number().int().min(0).max(100).optional().catch(undefined),
  espOthersMin: z.coerce.number().int().min(0).max(100).optional().catch(undefined),
  espOthersMax: z.coerce.number().int().min(0).max(100).optional().catch(undefined),
  // Domain filter. Collapse empty/whitespace to undefined so an inactive
  // filter leaves no URL key (mirrors `tags`). Still a plain trimmed string,
  // but now filters by exact match (case-insensitive) server-side instead of
  // a substring LIKE — the drawer's dropdown only ever writes a value taken
  // verbatim from `GET /email-accounts/domains`, so exact match is always
  // what the UI means by "this domain."
  domain: z
    .string()
    .optional()
    .transform((v) => {
      const trimmed = v?.trim();
      return trimmed && trimmed.length > 0 ? trimmed : undefined;
    })
    .catch(undefined),
  /** Warm-up start-date range (ISO `YYYY-MM-DD`). `ws.warmupStartedAt` bounds. */
  startDateFrom: z.string().optional().catch(undefined),
  startDateTo: z.string().optional().catch(undefined),
  /** Will send today: in a sending state (active, throttled, maintenance —
   * never paused, suspended, disconnected or in setup) with an ENABLED
   * warm-up schedule slot on today's weekday, in the account's own timezone —
   * a slot already run today still counts, same as one still ahead (see the
   * backend's `scheduledToday` DTO field). Only the
   * exact string/literal `true` turns it on, mirroring the activity tab's
   * `engagedOnly` convention — a `?scheduledToday=false` URL otherwise reads
   * as the truthy non-empty STRING `'false'`. Absent/false collapses to
   * `undefined` so an inactive filter leaves no URL key. */
  scheduledToday: z
    .union([z.literal('true'), z.literal(true)])
    .optional()
    .transform((v) => (v === 'true' || v === true ? true : undefined))
    .catch(undefined),
  // OAuth-return signals from the backend's email-accounts callback controller.
  // `connected` is the newly-created account id; `error` is one of the stable
  // codes mapped in EmailAccountsCallbackController.toErrorCode (`oauth_failed`,
  // `invalid_oauth_state`, `oauth_state_expired`, `email_already_connected`,
  // `plan_limit_reached`, `no_refresh_token`). Both are wiped from the URL
  // after the page handles them so a refresh doesn't re-fire the toast.
  connected: z.coerce.number().int().optional(),
  error: z.string().optional(),
});

export type EmailAccountsSearch = z.infer<typeof emailAccountsSearchSchema>;

/**
 * Search schema for `/email-accounts/connect` (`ConnectAccountPage`) — the
 * dedicated connect-an-account route that replaced the two-pane
 * `ConnectAccountDialog` modal.
 */
export const connectAccountSearchSchema = z.object({
  // Preselects a catalog entry by id (`CatalogItem.id` in
  // `provider-catalog.ts`, e.g. 'google-workspace' | 'outlook' |
  // 'custom-smtp' | a fetched SMTP preset id like 'yahoo'). Permissive by
  // design — the id space spans static entries plus server-fetched presets
  // the schema can't enumerate; an unknown id just fails to resolve and the
  // page falls back to the default (first OAuth provider).
  provider: z.string().optional().catch(undefined),
  // Active method card within a `family` row; ignored otherwise, and an unknown id
  // falls back to the first card. Separate from `provider` so both are linkable.
  method: z.string().optional().catch(undefined),
  // DWD bulk-connect deep-link: the Microsoft admin-consent callback
  // (`ConnectMailboxCallback`) navigates here with the newly created
  // workspace's id so the page opens straight into the bulk-workspace pane's
  // mailbox-select view. Coerced from the URL string; absent/garbage →
  // undefined.
  bulkWorkspace: z.coerce.number().int().optional(),
  // Paired with `bulkWorkspace`: 'manage' opens straight into that workspace's
  // Manage Mailboxes view instead of the default first-sync mailbox-select —
  // used by the Action items page's DWD reconnect link (TRU-1257 follow-up),
  // where the workspace is already connected and the user needs the per-
  // mailbox Reconnect action, not the sync flow. Absent/garbage → undefined,
  // same as every other param here.
  bulkWorkspaceView: z.literal('manage').optional().catch(undefined),
});

export type ConnectAccountSearch = z.infer<typeof connectAccountSearchSchema>;
